// BLP Chat Client - Integrated with Pinecone RAG + Groq LLM
// Provides intelligent responses for insurance agent conversations

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  message: string;
  error?: string;
  processing_time_ms?: number;
  retrieved_chunks?: number;
  categories_found?: string[];
  sources?: string[];
  ai_provider?: 'groq' | 'openai';
  fallback_reason?: string | null;
}

/**
 * Send messages to BLP AI system via Vercel API route
 * Uses Pinecone vector search + Groq LLM for intelligent responses
 */
export async function sendMessage(messages: ChatMessage[], tone?: string): Promise<ChatResponse> {
  try {
    console.log('🚀 BLP AI: Sending message to /api/chat');
    console.log('📝 Messages:', messages.length, 'total');
    console.log('🎭 Speaking tone:', tone || 'default');
    console.log('📝 Last message content:', messages[messages.length - 1]?.content);
    
    const startTime = Date.now();
    
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        messages,
        speaking_tone: tone || 'professional'
      })
    });

    const clientTime = Date.now() - startTime;
    console.log(`⚡ BLP AI: Client request completed in ${clientTime}ms`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ BLP AI: API error:', response.status, response.statusText, errorText);
      
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    console.log('✅ BLP AI: Response received');
    console.log('🔍 [simple-chat-client] Full response data:', data);
    console.log('🔍 [simple-chat-client] Response keys:', Object.keys(data));
    console.log('📊 Processing time:', data.processing_time_ms + 'ms');
    console.log('🎯 Retrieved chunks:', data.retrieved_chunks || 0);
    console.log('📂 Categories found:', data.categories_found?.join(', ') || 'none');
    console.log('📄 Sources used:', data.sources?.length || 0);
    console.log('🔍 [simple-chat-client] ai_provider field:', data.ai_provider === 'groq' ? 'G' : 'O');
    console.log('🔍 [simple-chat-client] fallback_reason field:', data.fallback_reason);
    
    if (data.error) {
      console.warn('⚠️ BLP AI: API returned error:', data.error);
    }

    const returnData = {
      message: data.message || '',
      error: data.error || null,
      processing_time_ms: data.processing_time_ms,
      retrieved_chunks: data.retrieved_chunks,
      categories_found: data.categories_found,
      sources: data.sources,
      // 🔍 DEBUG: Pass through AI provider fields
      ai_provider: data.ai_provider,
      fallback_reason: data.fallback_reason
    };

    console.log('🔍 [simple-chat-client] Returning data with ai_provider:', returnData.ai_provider === 'groq' ? 'G' : 'O');
    console.log('🔍 [simple-chat-client] Returning data with fallback_reason:', returnData.fallback_reason);

    return returnData;

  } catch (error) {
    console.error('❌ BLP AI: Request failed:', error);
    
    return {
      message: `**ACKNOWLEDGE THEIR CONCERN:**
I understand you have a question about the BLP.

**THE BLP SOLUTION:**
I apologize, but I'm experiencing technical difficulties accessing the AI system right now. The BLP provides 24-48 hour access to funds without death certificates, which is much faster than traditional processes.

**NEXT STEP/CLARIFY:**
[ASK]: "Can you tell me more about your specific situation while I work on getting you detailed information?"
[PROSPECT]: "When will the system be working normally again?"`,
      error: error instanceof Error ? error.message : 'Network error occurred',
      processing_time_ms: 0,
      retrieved_chunks: 0,
      categories_found: [],
      sources: []
    };
  }
}

// Production mode - real AI integration active
export function isDemoMode(): boolean {
  return false;
}